import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import Sidebar from '../../components/admin/Sidebar';
import TopBar from '../../components/admin/TopBar';
import { supabase } from '../../lib/supabase/client';
import { IslandProvider, useIslandState } from '../../components/island/IslandProvider';
import { DynamicIsland } from '../../components/island/DynamicIsland';
import ShaderBackground from '../../components/ShaderBackground';
import { EmailNavProvider } from './emails/EmailNavContext';
import { useAnrufe } from '../../components/island/useAnrufe';
import { ImmoSidebar } from '../../components/immobilien/sidebar/ImmoSidebar';
import { ImmobilienInhalt } from '../../components/immobilien/ImmobilienInhalt';
import { istImmobilienBereich } from '../../lib/immobilien/sidebarNav';
import { nutztMobileNav, useMobilModus } from '../../lib/immobilien/mobil';
import { MobileLeiste } from '../../components/immobilien/sidebar/MobileLeiste';

interface AdminLayoutProps {
  userProfile?: any;
  onSearchNavigate?: (path: string, id: string, type: string) => void;
}

// Der Burger sitzt oben links, die Pille oben mittig — sobald die Pille auf
// Handy-Breite aufgeht (Deck/Formular), überlagern sich beide. Deshalb blendet
// der Knopf aus, solange die Pille offen ist (Gegenstück: die Pille versteckt
// sich bei offenem Drawer). Eigene Komponente, weil useIslandState() nur
// INNERHALB des IslandProvider gelesen werden darf.
const MobilerMenueKnopf: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { mode } = useIslandState();
  if (mode === 'deck' || mode === 'form') return null;
  return (
    <button
      onClick={onOpen}
      aria-label="Menü öffnen"
      className="lg:hidden fixed left-[calc(0.75rem+env(safe-area-inset-left))] top-[calc(0.25rem+env(safe-area-inset-top))] lg:top-[calc(0.75rem+env(safe-area-inset-top))] z-[60] p-2.5 bg-white border border-gray-100 rounded-xl text-gray-900 shadow-sm active:scale-95 transition-all"
    >
      <Menu size={20} />
    </button>
  );
};

/** Hängt den Anruf-Wächter unterhalb des IslandProvider ein — useIslandActions
 *  funktioniert nur innerhalb des Providers. */
const AnrufWaechter: React.FC<{ aktiv: boolean }> = ({ aktiv }) => {
  useAnrufe(aktiv);
  return null;
};

const AdminLayout: React.FC<AdminLayoutProps> = ({
  userProfile,
  onSearchNavigate,
}) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Immobilienbereich im neuen Design (design_handoff_immobilien): eigene Fläche,
  // Kontext-Sidebar, kein Shader. Alle anderen Bereiche bleiben unverändert.
  const immo = istImmobilienBereich(location.pathname);
  // Bereiche mit unterer Leiste (MobileNav): unter 900 px Breite / 500 px Höhe ohne Sidebar und Menü-Knopf
  const mobilModus = useMobilModus();
  const mobilLeiste = immo && mobilModus.mobil && nutztMobileNav(location.pathname);
  const navigiere = (p: string) => { navigate(p); setIsMobileSidebarOpen(false); };

  return (
    <IslandProvider onSearchNavigate={onSearchNavigate}>
     <EmailNavProvider>
      <AnrufWaechter aktiv={!!userProfile} />
      <div className={immo ? 'immo-flaeche flex w-full' : 'flex min-h-screen w-full bg-transparent relative'}>
        {immo ? <div aria-hidden className="immo-wand" /> : <ShaderBackground />}
        {/* data-island: Marker fuer die Aussen-Klick-Handler der Detail-Sidebars, damit Klicks in die
            Pille (z.B. ein per openForm geoeffnetes Formular) nicht als "ausserhalb" gewertet werden
            und die Sidebar samt Eingaben schliessen. */}
        <div data-island>
          <DynamicIsland mobileNavOpen={isMobileSidebarOpen} />
        </div>

        {mobilLeiste
          ? <MobileLeiste quer={mobilModus.quer} userRole={userProfile?.role} allowedModules={userProfile?.allowed_modules} onNavigate={navigiere} />
          : <MobilerMenueKnopf onOpen={() => setIsMobileSidebarOpen(true)} />}

        {isMobileSidebarOpen && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] lg:hidden animate-in fade-in duration-300" onClick={() => setIsMobileSidebarOpen(false)} />
        )}

        {immo ? (mobilLeiste ? null : (
          <ImmoSidebar
            onNavigate={navigiere}
            onLogout={() => supabase.auth.signOut()}
            userEmail={userProfile?.email}
            userRole={userProfile?.role}
            avatarUrl={userProfile?.avatar_url}
            fullName={userProfile?.full_name}
            allowedModules={userProfile?.allowed_modules}
            isOpen={isMobileSidebarOpen}
            onClose={() => setIsMobileSidebarOpen(false)}
          />
        )) : (
        <Sidebar
          activePath={location.pathname}
          onNavigate={navigiere}
          onLogout={() => supabase.auth.signOut()}
          userEmail={userProfile?.email}
          userRole={userProfile?.role}
          avatarUrl={userProfile?.avatar_url}
          fullName={userProfile?.full_name}
          allowedModules={userProfile?.allowed_modules}
          isOpen={isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
        />
        )}

        {immo ? (
          <ImmobilienInhalt mobil={mobilLeiste ? { quer: mobilModus.quer } : null}>
            <Outlet />
          </ImmobilienInhalt>
        ) : (
        <main className="flex-1 flex flex-col min-h-screen overflow-hidden relative z-10">
          {/* Fixe Milchglas-Fläche über dem Shader (nur Content-Bereich). Liegt bewusst auf
              einem NICHT scrollenden Element: backdrop-blur auf dem Scroll-Container selbst
              führt zu ruckeligem/doppeltem Scrollen und einem mitwandernden Hintergrund.
              So bleibt der Hintergrund fix und das Scrollen flüssig — randlos, ohne Rundungen. */}
          <div aria-hidden className="absolute inset-0 bg-white/70 backdrop-blur-2xl pointer-events-none" />
          {/* Top-Leiste: links Einklappen + Bereich, Mitte die schwebende Pille, rechts Profil */}
          <TopBar
            fullName={userProfile?.full_name}
            role={userProfile?.role}
            email={userProfile?.email}
            avatarUrl={userProfile?.avatar_url}
            onLogout={() => supabase.auth.signOut()}
          />
          <div className="relative flex-1 overflow-y-auto px-4 lg:px-8 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] lg:pl-8 lg:pr-8 pb-[max(0.75rem,env(safe-area-inset-bottom))] custom-scrollbar">
            <div className="max-w-[1600px] mx-auto pt-5 kurz:pt-2">
              <Outlet />
            </div>
          </div>
        </main>
        )}
      </div>
     </EmailNavProvider>
    </IslandProvider>
  );
};

export default AdminLayout;